import React, { useState, useEffect } from "react";
import "./popupSlot.css";

/* ===== Helpers génériques ===== */
const safeSplit = (str, sep) => (typeof str === "string" ? str.split(sep) : []);
const parseIntSafe = (v, def = 0) => {
  const n = parseInt(v, 10);
  return Number.isFinite(n) ? n : def;
};
const setDateFromHHMM = (base, hhmm) => {
  if (typeof hhmm !== "string" || !hhmm.includes(":")) return null;
  const [h, m] = safeSplit(hhmm, ":").map((x) => parseIntSafe(x));
  const d = new Date(base);
  d.setHours(h, m, 0, 0);
  return d;
};
const toFRDate = (iso) =>
  new Date(iso).toLocaleDateString("fr-FR", { dateStyle: "full" });
const toFRTime = (iso) =>
  new Date(iso).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
const formatEUR = (n) => {
  if (n == null) return "";
  try {
    return new Intl.NumberFormat("fr-FR", {
      style: "currency",
      currency: "EUR",
      maximumFractionDigits: 2,
    }).format(Number(n));
  } catch {
    const x = Number(n);
    return isFinite(x) ? `${x.toFixed(2)} €` : "";
  }
};
// FR -> E.164 (+33)
const normalizePhone = (p) => {
  if (!p) return "";
  let s = String(p).trim().replace(/\s+/g, "");
  if (s.startsWith("+")) return s;
  if (s.startsWith("0")) return "+33" + s.slice(1);
  return s;
};

/* ===== HTTP helpers ===== */
async function getJSON(url, headers) {
  const res = await fetch(url, { headers });
  if (!res.ok) {
    const t = await res.text().catch(() => "");
    throw new Error(`${url} ${res.status}: ${t}`);
  }
  return res.json();
}
async function postJSON(url, body, headers) {
  const res = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...(headers || {}) },
    body: JSON.stringify(body),
  });
  const text = await res.text().catch(() => "");
  if (!res.ok) {
    // log utile pour débug
    console.error("[HTTP]", url, res.status, text);
    throw new Error(`${url} ${res.status}: ${text}`);
  }
  try {
    return JSON.parse(text);
  } catch {
    return {};
  }
}

/* ===== Envois immédiats (email + sms) ===== */
async function notifyNow({ API, headers, client, pro, rdvISO, lieu, service, price, proSociete }) {
  const tasks = [];
  const dateTxt = toFRDate(rdvISO);
  const timeTxt = toFRTime(rdvISO);
  const prixTxt = price != null ? formatEUR(price) : "";

  // --- CLIENT EMAIL ---
  if (client.email) {
    const subject = "Votre rendez-vous est confirmé ✅";
    const text = [
      `Bonjour ${client.prenom || ""} ${client.nom || ""}`.trim(),
      "",
      `Merci pour votre rendez-vous chez ${proSociete || pro.nom || "votre professionnel"}, le ${dateTxt} à ${timeTxt}.`,
      service ? `Prestation : ${service}${prixTxt ? ` — ${prixTxt}` : ""}` : "",
      lieu ? `Lieu : ${lieu}` : "",
      pro.phone ? `Si besoin : ${pro.phone}.` : "",
      "",
      "À très bientôt 👋",
    ]
      .filter(Boolean)
      .join("\n");
    tasks.push(
      postJSON(`${API}/api/notifications/email`, { to: client.email, subject, text }, headers)
        .then(() => ({ ok: true, target: "client.email" }))
        .catch((e) => ({ ok: false, target: "client.email", error: String(e) }))
    );
  }

  // --- CLIENT SMS ---
  if (client.phone) {
    const text = [
      "RDV confirmé",
      proSociete ? `chez ${proSociete}` : "",
      `le ${dateTxt} à ${timeTxt}`,
      service ? `– ${service}` : "",
      prixTxt ? `(${prixTxt})` : "",
      pro.phone ? `Tel pro: ${pro.phone}` : "",
    ]
      .filter(Boolean)
      .join(" ");
    tasks.push(
      postJSON(
        `${API}/api/notifications/sms`,
        { to: normalizePhone(client.phone), text },
        headers
      )
        .then(() => ({ ok: true, target: "client.sms" }))
        .catch((e) => ({ ok: false, target: "client.sms", error: String(e) }))
    );
  }

  // --- PRO EMAIL ---
  if (pro.email) {
    const subject = "Nouveau rendez-vous reçu 📅";
    const text = [
      `Bonjour ${pro.nom || proSociete || "Professionnel"}`,
      "",
      `Vous avez un nouveau rendez-vous :`,
      `• Date : ${dateTxt}`,
      `• Heure : ${timeTxt}`,
      service ? `• Prestation : ${service}${prixTxt ? ` — ${prixTxt}` : ""}` : "",
      lieu ? `• Lieu : ${lieu}` : "",
      "",
      "Client :",
      `• Nom : ${client.prenom || ""} ${client.nom || ""}`.trim(),
      client.phone ? `• Téléphone : ${client.phone}` : "",
      client.email ? `• E-mail : ${client.email}` : "",
    ]
      .filter(Boolean)
      .join("\n");
    tasks.push(
      postJSON(`${API}/api/notifications/email`, { to: pro.email, subject, text }, headers)
        .then(() => ({ ok: true, target: "pro.email" }))
        .catch((e) => ({ ok: false, target: "pro.email", error: String(e) }))
    );
  }

  const results = await Promise.all(tasks);
  if (localStorage.getItem("debugNotifs") === "1") {
    console.group("[notifs] résultats envois immédiats");
    results.forEach((x) => console[x.ok ? "log" : "error"](x));
    console.groupEnd();
  }
  return results;
}

/* ===== Composant ===== */
const PopupSlot = ({ visible, onClose, onSave, slotTime, annonceId }) => {
  const API = process.env.REACT_APP_API_URL || "http://localhost:5000";
  const token = localStorage.getItem("token");
  const authHeaders = {
    "Content-Type": "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };

  const [activities, setActivities] = useState([]);
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [existingSlots, setExistingSlots] = useState([]);
  const [workHours, setWorkHours] = useState([]);
  const [horairesDepasse, setHorairesDepasse] = useState(false);
  const [annonce, setAnnonce] = useState(null); // pour récupérer adresse / company

  // Infos client (saisies)
  const [clientNom, setClientNom] = useState("");
  const [clientPrenom, setClientPrenom] = useState("");
  const [clientEmail, setClientEmail] = useState("");
  const [clientPhone, setClientPhone] = useState("");

  // Date & heure
  const initial = slotTime ? new Date(slotTime) : new Date();
  const [date, setDate] = useState(initial.toISOString().slice(0, 10));
  const [heure, setHeure] = useState(
    `${initial.getHours().toString().padStart(2, "0")}:${initial
      .getMinutes()
      .toString()
      .padStart(2, "0")}`
  );
  const [endDate, setEndDate] = useState(null);

  // Toujours à jour via localStorage
  const userId = localStorage.getItem("userId"); // pro actuel
  const selectedProfilId = localStorage.getItem("selectedProfilId");

  // Date min
  const todayString = new Date().toISOString().split("T")[0];

  // ——— Load annonces, activités, slots et horaires ———
  useEffect(() => {
    const fetchData = async () => {
      try {
        // 1) Annonce (par user)
        const resAnnonce = await getJSON(
          `${API}/api/annonces/by-user/${userId}`,
          authHeaders
        );
        const a = Array.isArray(resAnnonce) ? resAnnonce[0] : resAnnonce;
        setAnnonce(a || null);

        // 2) Activités + horaires (profil > entreprise)
        let activitiesList = [];

        if (selectedProfilId && a && Array.isArray(a?.profils)) {
          const idx = Number.isFinite(+selectedProfilId) ? +selectedProfilId : null;
          const profil =
            a.profils.find(
              (p) => p?._id === selectedProfilId || p?._id?.toString() === selectedProfilId
            ) ?? (idx !== null ? a.profils[idx] : null);

          if (profil && Array.isArray(profil.activities)) {
            activitiesList = profil.activities.map((act, i) => {
              const durationHours = parseIntSafe(act.durationHours);
              const durationMinutes = parseIntSafe(act.durationMinutes);
              return {
                id: (profil._id || "p") + "-" + i,
                name: act.name,
                duration: durationHours * 60 + durationMinutes,
                durationHours,
                durationMinutes,
              };
            });
          }
          if (profil && Array.isArray(profil.workingHours)) {
            setWorkHours(profil.workingHours);
          }
        } else if (a && Array.isArray(a?.professions)) {
          activitiesList = a.professions.flatMap((prof, pidx) =>
            (prof.activities || []).map((act, i) => {
              const dh = parseIntSafe(act.durationHours);
              const dm = parseIntSafe(act.durationMinutes);
              return {
                id: `${pidx}-${i}`,
                name: `[${prof.profession}] ${act.name}`,
                duration: dh * 60 + dm,
                durationHours: dh,
                durationMinutes: dm,
              };
            })
          );
          if (Array.isArray(a.workHours)) setWorkHours(a.workHours);
        } else if (Array.isArray(a?.workHours)) {
          setWorkHours(a.workHours);
        }

        setActivities(activitiesList);

        // 3) Slots existants (pour chevauchement)
        let urlSlots = `${API}/api/slotsCalendar/pro/${userId}`;
        if (selectedProfilId) urlSlots += `?profilId=${selectedProfilId}`;
        const slots = await getJSON(urlSlots, authHeaders);
        const formatted = Array.isArray(slots)
          ? slots.map((s) => ({ start: new Date(s.start), end: new Date(s.end) }))
          : [];
        setExistingSlots(formatted);
      } catch (err) {
        console.error("❌ Erreur de chargement :", err);
      }
    };

    if (visible) {
      fetchData();
      // reset du formulaire à l’ouverture
      setClientNom("");
      setClientPrenom("");
      setClientEmail("");
      setClientPhone("");
      setSelectedActivity(null);
      const init = slotTime ? new Date(slotTime) : new Date();
      setDate(init.toISOString().slice(0, 10));
      setHeure(
        `${init.getHours().toString().padStart(2, "0")}:${init
          .getMinutes()
          .toString()
          .padStart(2, "0")}`
      );
      setEndDate(null);
      setHorairesDepasse(false);
    }
    // eslint-disable-next-line
  }, [visible, userId, slotTime, selectedProfilId, API, token]);

  // Calcule l’heure de fin et vérifie les horaires pro
  useEffect(() => {
    if (selectedActivity && date && heure && Array.isArray(workHours) && workHours.length > 0) {
      const [h, m] = safeSplit(heure, ":").map((x) => parseIntSafe(x));
      const startDate = new Date(date);
      startDate.setHours(h, m, 0, 0);

      const end = new Date(startDate.getTime() + (selectedActivity.duration || 0) * 60000);
      setEndDate(end);

      const jourFR = startDate.toLocaleDateString("fr-FR", { weekday: "long" }).toLowerCase();
      const workDay = workHours.find((wh) => (wh?.day || "").toLowerCase().startsWith(jourFR));

      let depasse = false;
      if (workDay && workDay.isOpen) {
        if (Array.isArray(workDay.slots) && workDay.slots.length > 0) {
          const insideOneSlot = workDay.slots.some((slot) => {
            const s = setDateFromHHMM(startDate, slot?.start);
            const e = setDateFromHHMM(startDate, slot?.end);
            if (!s || !e) return false;
            return startDate >= s && end <= e;
          });
          depasse = !insideOneSlot;
        } else if (workDay.start || workDay.end) {
          const dateDeb = setDateFromHHMM(startDate, workDay.start) || new Date(startDate);
          const dateFin = setDateFromHHMM(startDate, workDay.end) || new Date(startDate);
          depasse = startDate < dateDeb || end > dateFin;
        } else {
          depasse = true;
        }
      } else {
        depasse = true;
      }
      setHorairesDepasse(depasse);
    } else {
      setHorairesDepasse(false);
      setEndDate(null);
    }
  }, [selectedActivity, date, heure, workHours]);

  const isPast = () => {
    if (!date || !heure) return false;
    const [h, m] = safeSplit(heure, ":");
    const checkDate = new Date(date);
    checkDate.setHours(parseIntSafe(h), parseIntSafe(m), 0, 0);
    return checkDate < new Date();
  };

  // Validation et sauvegarde + ENVOIS
  const handleSave = async () => {
    if (!annonceId) {
      alert("Erreur : annonceId manquant, réessayez dans 1 seconde.");
      return;
    }
    if (!clientNom || !clientPrenom || !clientEmail || !clientPhone || !selectedActivity) {
      alert("❌ Veuillez remplir toutes les informations du client + activité.");
      return;
    }
    if (localStorage.getItem("role") === "pro" && !selectedProfilId) {
      alert("Sélectionnez d'abord un profil dans le header !");
      return;
    }
    const [h, m] = safeSplit(heure, ":").map((x) => parseIntSafe(x));
    const start = new Date(date);
    start.setHours(h, m, 0, 0);
    if (start < new Date()) {
      alert("❌ Vous ne pouvez pas choisir une date/heure dans le passé.");
      return;
    }
    const end = endDate;

    try {
      // Vérifie que le client existe (comme avant)
      const res = await fetch(
        `${API}/api/users/by_email/${encodeURIComponent(clientEmail)}`,
        { headers: authHeaders }
      );
      if (!res.ok) {
        alert("❌ Aucun client trouvé avec cette adresse email.");
        return;
      }

      // Chevauchement
      const isOverlapping = existingSlots.some((s) => start < s.end && end > s.start);
      if (isOverlapping) {
        alert("❌ Ce créneau chevauche un autre rendez-vous.");
        return;
      }

      const apiDate = start.toLocaleDateString("fr-FR");
      const apiHeure = start.toTimeString().slice(0, 5);

      // ——— Création côté parent (ta logique) ———
      onSave({
        title: selectedActivity.name,
        description: `Client: ${clientPrenom} ${clientNom}, Email: ${clientEmail}, Tel: ${clientPhone}`,
        duration: selectedActivity.duration,
        emailUser: clientEmail,
        date: apiDate,
        heure: apiHeure,
        startDate: start,
        annonceId,
        profilId: selectedProfilId,
      });

      // ——— Récup PRO depuis l’inscription (userId courant) ———
      let proEmail = "";
      let proPhone = "";
      let proNom = "";

      try {
        const pro = await getJSON(`${API}/api/users/${userId}`, {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        });
        proEmail = pro?.email || "";
        proPhone = pro?.telephone || "";
        proNom = pro?.nom || pro?.name || "";
      } catch (e) {
        console.error("[pro fetch] error:", e);
      }

      // Info lieu / société depuis l’annonce si dispo
      const proSociete =
        annonce?.nameCompany || annonce?.userId?.nameCompany || annonce?.userId?.societe || "";
      const lieu = annonce?.adresse || annonce?.address || "";

      // ——— ENVOI immédiat des notifs au client + pro ———
      const payload = {
        API,
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        client: {
          email: clientEmail,
          phone: normalizePhone(clientPhone),
          nom: clientNom,
          prenom: clientPrenom,
        },
        pro: {
          email: proEmail,
          phone: normalizePhone(proPhone),
          nom: proNom,
        },
        rdvISO: start.toISOString(),
        lieu,
        service: selectedActivity.name,
        price: null, // si tu as un prix, mets-le ici
        proSociete,
      };

      try {
        await notifyNow(payload);
      } catch (e) {
        console.error("[notifs] error:", e);
      }

      alert("✅ Créneau enregistré et notifications envoyées !");
      onClose();
    } catch (err) {
      console.error("❌ Erreur lors de la vérification du client :", err);
      alert("❌ Erreur serveur lors de la vérification du client.");
    }
  };

  if (!visible) return null;

  return (
    <div className="popup-overlay">
      <div className="popup-content">
        <h3>Ajouter un créneau</h3>

        <label>Date</label>
        <input
          type="date"
          value={date}
          min={todayString}
          onChange={(e) => setDate(e.target.value)}
        />

        <label>Heure</label>
        <input
          type="time"
          value={heure}
          onChange={(e) => setHeure(e.target.value)}
        />

        {selectedActivity && date && heure && (
          <div style={{ margin: "8px 0" }}>
            <b>Heure de fin estimée :</b>{" "}
            {endDate ? endDate.toLocaleString("fr-FR") : ""}
          </div>
        )}

        <input
          placeholder="Nom du client"
          value={clientNom}
          onChange={(e) => setClientNom(e.target.value)}
        />
        <input
          placeholder="Prénom du client"
          value={clientPrenom}
          onChange={(e) => setClientPrenom(e.target.value)}
        />
        <input
          placeholder="Email du client"
          value={clientEmail}
          onChange={(e) => setClientEmail(e.target.value)}
        />
        <input
          placeholder="Téléphone du client"
          value={clientPhone}
          onChange={(e) => setClientPhone(e.target.value)}
        />

        <select
          value={selectedActivity?.id || ""}
          onChange={(e) => {
            const selected = activities.find(
              (a) => a.id.toString() === e.target.value
            );
            setSelectedActivity(selected || null);
          }}
        >
          <option value="">-- Choisir une activité --</option>
          {activities.map((activity) => (
            <option key={activity.id} value={activity.id}>
              {activity.name} ({activity.duration} min)
            </option>
          ))}
        </select>

        {activities.length === 0 && (
          <p style={{ color: "red", marginTop: "10px" }}>
            ⚠️ Aucune activité disponible. Avez-vous bien enregistré une annonce
            ou choisi un profil ?
          </p>
        )}

        {horairesDepasse && (
          <div style={{ color: "red", marginTop: 8, fontWeight: 600 }}>
            ❌ Ce créneau dépasse les horaires d'ouverture du professionnel.
          </div>
        )}

        <div className="popup-actions">
          <button
            onClick={handleSave}
            disabled={isPast() || horairesDepasse}
            style={
              isPast() || horairesDepasse
                ? { opacity: 0.6, pointerEvents: "none" }
                : {}
            }
          >
            Enregistrer
          </button>
          <button onClick={onClose}>Annuler</button>
        </div>

        {isPast() && (
          <div style={{ color: "red", marginTop: "8px" }}>
            🚫 Impossible de réserver dans le passé.
          </div>
        )}
      </div>
    </div>
  );
};

export default PopupSlot;
